import type { SortState } from '@/types/table';
import { OrderStatusEnum } from '../table-cell/model/orderCell';
import { sortSurvivesView } from './sortScope';

export type OrderViewKey = 'all' | 'in-progress' | 'completed' | 'canceled';

export interface OrderView {
  key: OrderViewKey;
  name: string;
  /** Locked Order Status set for this view, or null when fully editable. */
  lockedStatuses: OrderStatusEnum[] | null;
  /**
   * Sort sent when the user has not picked one; null defers to the backend
   * default (updatedAt desc). Completed sorts by completion, newest first
   * (DV-3122): on accounts migrated from PlanoX, updatedAt tracks bridge
   * activity rather than delivery, so the backend default reads as random.
   * Also scopes an explicit sort on this column to this view (see sortScope).
   */
  defaultSort: SortState | null;
}

export const ORDER_VIEWS: OrderView[] = [
  { key: 'all', name: 'All Orders', lockedStatuses: null, defaultSort: null },
  {
    key: 'in-progress',
    name: 'In Progress',
    lockedStatuses: [
      OrderStatusEnum.RECEIVED,
      OrderStatusEnum.SCHEDULED,
      OrderStatusEnum.AWAITING_RESCHEDULE,
      OrderStatusEnum.APPOINTMENT_COMPLETE,
      OrderStatusEnum.PROCESSING,
    ],
    defaultSort: null,
  },
  {
    key: 'completed',
    name: 'Completed',
    lockedStatuses: [
      OrderStatusEnum.DELIVERED,
      OrderStatusEnum.UNABLE_TO_COMPLETE,
    ],
    defaultSort: { selector: 'orderCompletedDate', asc: false },
  },
  {
    key: 'canceled',
    name: 'Canceled',
    lockedStatuses: [OrderStatusEnum.CANCELLED],
    defaultSort: null,
  },
];

const ALL_VIEW = ORDER_VIEWS[0];

/**
 * Resolve a `?view=` key to its OrderView. Unknown/absent keys fall back to
 * All Orders. This is the single seam a future data-driven saved-views
 * feature replaces — callers never branch on the key themselves.
 */
export const resolveOrderView = (
  key: string | null | undefined
): OrderView => ORDER_VIEWS.find((v) => v.key === key) ?? ALL_VIEW;

/** Whether a chosen sort still applies on the client view `viewKey` (see sortScope). */
export const orderSortSurvivesView = (
  sort: SortState | null,
  viewKey: OrderViewKey | null
): boolean => sortSurvivesView(ORDER_VIEWS, sort, viewKey);

/**
 * The `?view=` query for a view key, normalized through `resolveOrderView`:
 * `{}` for All Orders (the bare route) and for any key that isn't a view, so
 * a tampered param is never passed on (DV-3455). Shared by the links into the
 * asset workspace and its Back, so the two cannot disagree on the encoding.
 */
export const orderViewQuery = (
  key: string | null | undefined
): { view?: OrderViewKey } => {
  const view = resolveOrderView(key);
  return view.key === ALL_VIEW.key ? {} : { view: view.key };
};
